<template>
    <div class="ai-tool-page">
        <!-- Service Unavailable Banner -->
        <div v-if="!serviceAvailable" class="ai-tool-unavailable">
            <div class="unavailable-icon">
                <i class="fa fa-wrench"></i>
            </div>
            <h3>AI Tool is temporarily unavailable</h3>
            <p>
                The AI analysis service is currently being configured by the administrator. Please
                check back later.
            </p>
        </div>

        <div v-else class="ai-tool-content">
            <!-- LEFT PANEL: Input -->
            <div class="ai-tool-input-panel">
                <!-- File Upload Section -->
                <div class="ai-tool-section-header">
                    <i class="fa fa-upload"></i> Upload Content
                    <span class="section-optional">(Valgfrit)</span>
                </div>
                <div class="ai-tool-upload-area">
                    <div
                        v-if="!uploadedFile"
                        class="ai-tool-dropzone"
                        @dragover.prevent="isDragging = true"
                        @dragleave="isDragging = false"
                        @drop.prevent="handleDrop"
                        :class="{ dragging: isDragging }"
                    >
                        <input
                            type="file"
                            ref="fileInput"
                            accept="image/*,video/*,application/pdf"
                            @change="handleFileSelect"
                            class="hidden-input"
                        />

                        <!-- Drag overlay -->
                        <div v-if="isDragging" class="drag-overlay">
                            <div class="drag-overlay-ring">
                                <i class="fa fa-cloud-upload"></i>
                            </div>
                            <span>Release to upload</span>
                        </div>

                        <div class="dropzone-content" @click="$refs.fileInput.click()">
                            <div class="dropzone-icon-ring">
                                <i class="fa fa-cloud-upload"></i>
                            </div>
                            <div class="dropzone-text">
                                <p class="dropzone-heading">Drop your file here</p>
                                <p class="dropzone-subtext">
                                    or <span class="dropzone-browse">click to browse</span> from
                                    your device
                                </p>
                            </div>
                            <div class="dropzone-formats">
                                <span class="format-chip chip-image"
                                    ><i class="fa fa-file-image-o"></i> Images</span
                                >
                                <span class="format-chip chip-video"
                                    ><i class="fa fa-file-video-o"></i> Videos</span
                                >
                                <span class="format-chip chip-pdf"
                                    ><i class="fa fa-file-pdf-o"></i> PDF</span
                                >
                            </div>
                            <span class="dropzone-limit"
                                ><i class="fa fa-info-circle"></i> Max file size: 100 MB</span
                            >
                        </div>
                    </div>

                    <!-- File Preview (when file is uploaded) -->
                    <div v-else class="ai-tool-file-preview">
                        <div class="ai-tool-preview-container">
                            <img
                                v-if="fileType === 'image'"
                                :src="filePreviewUrl"
                                class="preview-thumbnail"
                            />
                            <video
                                v-else-if="fileType === 'video'"
                                :src="filePreviewUrl"
                                class="preview-thumbnail"
                                controls
                            ></video>
                            <div v-else-if="fileType === 'pdf'" class="preview-pdf">
                                <div class="pdf-icon-wrap">
                                    <i class="fa fa-file-pdf-o"></i>
                                </div>
                                <span class="pdf-filename">{{ uploadedFile.name }}</span>
                                <span class="pdf-size">{{
                                    formatFileSize(uploadedFile.size)
                                }}</span>
                            </div>
                        </div>
                        <div class="preview-actions">
                            <div class="preview-file-info">
                                <div class="preview-file-icon-wrap" :class="'icon-' + fileType">
                                    <i class="fa" :class="getFileTypeIcon(fileType)"></i>
                                </div>
                                <div class="preview-file-meta">
                                    <span class="file-name" :title="uploadedFile.name">{{
                                        uploadedFile.name
                                    }}</span>
                                    <span class="file-size">{{
                                        formatFileSize(uploadedFile.size)
                                    }}</span>
                                </div>
                            </div>
                            <button
                                class="btn btn-sm btn-remove-file"
                                @click="clearFile"
                                title="Remove file"
                            >
                                <i class="fa fa-trash-o"></i> Remove
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Divider -->
                <div class="ai-tool-section-divider">
                    <span>AND / OR</span>
                </div>

                <!-- Copywriting Section -->
                <div class="ai-tool-section-header">
                    <i class="fa fa-pencil"></i> Copywriting
                    <span class="section-optional">Optional</span>
                </div>
                <div class="ai-tool-text-area">
                    <textarea
                        v-model="textContent"
                        class="form-control"
                        rows="6"
                        placeholder="Paste or type your copywriting here..."
                    ></textarea>
                    <div class="text-area-footer">
                        <span
                            v-if="textContent"
                            class="text-char-count"
                            :class="{ 'near-limit': textContent.length > 45000 }"
                        >
                            <i class="fa fa-info-circle"></i>
                            {{ textContent.length.toLocaleString() }} / 50,000 characters
                        </span>
                        <div v-if="textContent" class="text-actions">
                            <button
                                class="btn btn-sm btn-danger"
                                @click="clearText"
                                title="Clear text"
                            >
                                <i class="fa fa-trash"></i> Clear
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Hint text -->
                <p class="ai-tool-input-hint text-muted small mt-2">
                    <i class="fa fa-info-circle"></i>
                    Provide content, copywriting text, or both for AI analysis.
                </p>

                <!-- Analyze Button -->
                <button
                    class="btn btn-success btn-lg btn-block ai-tool-analyze-btn"
                    :disabled="!canAnalyze || isAnalyzing"
                    @click="analyze"
                >
                    <span v-if="isAnalyzing">
                        <i class="fa fa-spinner fa-spin"></i> Analyzing...
                    </span>
                    <span v-else> <i class="fa fa-magic"></i> Analyze Content </span>
                </button>
                <button
                    v-if="isAnalyzing"
                    class="btn btn-outline-secondary btn-block mt-2"
                    @click="cancelAnalysis"
                >
                    <i class="fa fa-times"></i> Cancel
                </button>
            </div>

            <!-- RIGHT PANEL: AI Response -->
            <div class="ai-tool-response-panel">
                <div class="response-header">
                    <h4>AI Feedback</h4>
                    <button
                        v-if="aiResponse"
                        class="btn btn-sm btn-outline-secondary"
                        @click="copyResponse"
                    >
                        <i class="fa fa-copy"></i> {{ copyButtonText }}
                    </button>
                </div>
                <div class="response-body">
                    <!-- Empty State -->
                    <div v-if="!aiResponse && !isAnalyzing" class="response-empty">
                        <i class="fa fa-robot fa-3x text-muted"></i>
                        <p class="text-muted">AI response will appear here</p>
                        <small class="text-muted"
                            >Upload a file or enter text, then click "Analyze Content"</small
                        >
                    </div>
                    <!-- Loading State -->
                    <div v-if="isAnalyzing" class="response-loading">
                        <i class="fa fa-spinner fa-spin fa-2x"></i>
                        <p>Analyzing your content...</p>
                    </div>
                    <!-- Response Content -->
                    <div
                        v-if="aiResponse && !isAnalyzing"
                        class="response-content"
                        v-html="parsedResponse"
                    ></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'

// Configure marked for safe, GFM-compatible rendering
const renderer = new marked.Renderer()
renderer.link = function (href, title, text) {
    var titleAttr = title ? ' title="' + title + '"' : ''
    return (
        '<a href="' +
        href +
        '"' +
        titleAttr +
        ' target="_blank" rel="noopener noreferrer">' +
        text +
        '</a>'
    )
}

marked.setOptions({
    breaks: true,
    gfm: true,
    renderer: renderer,
})

const responseDisclaimer =
    'Denne tjeneste er en AI-drevet guide, ikke en erstatning for professionel juridisk rådgivning. Den leverede feedback er beregnet til at hjælpe din interne review-proces. betterhype garanterer ikke for den juridiske gyldighed af disse forslag og er ikke ansvarlig for krav, der opstår som følge af brugen af dette værktøj.'

function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export default {
    props: {
        pageTitle: {
            type: String,
            default: 'AI Communication Advisor',
        },
        pageSubtitle: {
            type: String,
            default: 'Upload content or enter text to get AI-powered feedbackádad',
        },
        promptName: {
            type: String,
            default: 'communication-advisor',
        },
        serviceAvailable: {
            type: Boolean,
            default: true,
        },
        isBrand: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            uploadedFile: null, // File object
            filePreviewUrl: null, // Object URL for preview
            fileType: null, // 'image', 'video', 'pdf'
            fileBase64: null, // Base64 encoded file
            textContent: '', // Text input
            isDragging: false,
            isAnalyzing: false,
            abortController: null,
            aiResponse: '',
            error: null,
            // Enhanced UI state
            uploadProgress: 0,
            uploadError: null,
            uploadSuccess: false,
            copyButtonText: 'Copy', // Dynamic text for copy button
        }
    },

    computed: {
        hasContent() {
            return !!(this.uploadedFile || this.textContent)
        },
        canAnalyze() {
            return this.hasContent
        },
        parsedResponse() {
            if (!this.aiResponse) return ''
            let html = marked.parse(this.aiResponse)

            html = html
                // Section headers with emoji — "🛑 NON-COMPLIANT", "⚠️ DOKUMENTATION PÅKRÆVET", "🌫️ GRÅ ZONER", "✅ ANBEFALEDE ÆNDRINGER"
                .replace(
                    /(<p>)\s*(🛑\s*.*?)\s*(<\/p>)/gi,
                    '$1<span class="compliance-label compliance-non-compliant">$2</span>$3'
                )
                .replace(
                    /(<p>)\s*(⚠️\s*.*?)\s*(<\/p>)/gi,
                    '$1<span class="compliance-label compliance-documentation">$2</span>$3'
                )
                .replace(
                    /(<p>)\s*(🌫️\s*.*?)\s*(<\/p>)/gi,
                    '$1<span class="compliance-label compliance-grey-zone">$2</span>$3'
                )
                .replace(
                    /(<p>)\s*(✅\s*.*?)\s*(<\/p>)/gi,
                    '$1<span class="compliance-label compliance-recommended">$2</span>$3'
                )
                // Legacy Danish-only labels (no emoji)
                .replace(
                    /(<p>)\s*(Dokumentation)\s*(<\/p>)/g,
                    '$1<span class="compliance-label compliance-documentation">$2</span>$3'
                )
                .replace(
                    /(<p>)\s*(Visuelle anbefalinger)\s*(<\/p>)/g,
                    '$1<span class="compliance-label compliance-grey-zone">$2</span>$3'
                )
                .replace(
                    /(<p>)\s*(Opmærksomhedspunkter)\s*(<\/p>)/g,
                    '$1<span class="compliance-label compliance-grey-zone">$2</span>$3'
                )
                .replace(
                    /(<p>)\s*(✅\s*ANBEFALEDE ÆNDRINGER)\s*(<\/p>)/g,
                    '$1<span class="compliance-label compliance-recommended">$2</span>$3'
                )
                .replace(
                    new RegExp(`<p>${escapeRegExp(responseDisclaimer)}<\\/p>`, 'g'),
                    `<p class="response-disclaimer">${responseDisclaimer}</p>`
                )

            return DOMPurify.sanitize(html)
        },
    },

    methods: {
        handleFileSelect(event) {
            const file = event.target.files[0]
            if (file) this.processFile(file)
        },

        handleDrop(event) {
            this.isDragging = false
            const file = event.dataTransfer.files[0]
            if (file) this.processFile(file)
        },

        processFile(file) {
            // Reset states
            this.uploadError = null
            this.uploadSuccess = false
            this.uploadProgress = 0

            // Validate size (100MB)
            if (file.size > 100 * 1024 * 1024) {
                this.uploadError = 'Filen må maksimalt være 100MB'
                return
            }

            // Validate type
            const validTypes = ['image/', 'video/', 'application/pdf']
            if (!validTypes.some((t) => file.type.startsWith(t))) {
                this.uploadError = 'Unsupported file type. Use images, videos, or PDF.'
                return
            }

            // Simulate upload progress
            this.simulateUploadProgress(() => {
                this.uploadedFile = file
                this.filePreviewUrl = URL.createObjectURL(file)
                this.uploadSuccess = true

                if (file.type.startsWith('image/')) this.fileType = 'image'
                else if (file.type.startsWith('video/')) this.fileType = 'video'
                else if (file.type === 'application/pdf') this.fileType = 'pdf'

                // Convert to base64 for API
                var reader = new FileReader()
                reader.onload = (e) => {
                    this.fileBase64 = e.target.result.split(',')[1] // Remove data:... prefix
                }
                reader.readAsDataURL(file)
            })
        },

        simulateUploadProgress(callback) {
            const interval = setInterval(() => {
                this.uploadProgress += Math.random() * 30
                if (this.uploadProgress >= 90) {
                    this.uploadProgress = 90
                    clearInterval(interval)
                    setTimeout(() => {
                        this.uploadProgress = 100
                        setTimeout(callback, 200)
                    }, 200)
                }
            }, 100)
        },

        resetInput() {
            this.clearFile()
            this.textContent = ''
            this.error = null
            this.aiResponse = ''
        },

        clearText() {
            this.textContent = ''
        },

        clearFile() {
            if (this.filePreviewUrl) {
                URL.revokeObjectURL(this.filePreviewUrl)
            }
            this.uploadedFile = null
            this.filePreviewUrl = null
            this.fileType = null
            this.fileBase64 = null
            this.uploadProgress = 0
            this.uploadError = null
            this.uploadSuccess = false
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
            }
        },

        // Enhanced UI methods
        replaceFile() {
            this.clearFile()
            this.$nextTick(() => {
                this.$refs.fileInput.click()
            })
        },

        downloadFile() {
            if (this.filePreviewUrl) {
                const a = document.createElement('a')
                a.href = this.filePreviewUrl
                a.download = this.uploadedFile.name
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
            }
        },

        viewFullscreen() {
            if (this.fileType === 'image' && this.filePreviewUrl) {
                window.open(this.filePreviewUrl, '_blank')
            }
        },

        getFileTypeIcon(type) {
            switch (type) {
                case 'image':
                    return 'fa-file-image-o'
                case 'video':
                    return 'fa-file-video-o'
                case 'pdf':
                    return 'fa-file-pdf-o'
                default:
                    return 'fa-file-o'
            }
        },

        getFileTypeLabel(type) {
            switch (type) {
                case 'image':
                    return 'Image'
                case 'video':
                    return 'Video'
                case 'pdf':
                    return 'PDF'
                default:
                    return 'File'
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
        },

        analyze() {
            if (!this.hasContent || this.isAnalyzing) return

            this.isAnalyzing = true
            this.aiResponse = ''
            this.error = null

            if (this.abortController) {
                this.abortController.abort()
            }
            this.abortController = new AbortController()

            var formData = new FormData()

            var hasFile = !!this.uploadedFile
            var hasText = this.textContent.trim().length > 0

            if (hasFile && hasText) {
                formData.append('input_type', 'both')
                formData.append('file_input', this.uploadedFile)
                formData.append('text_input', this.textContent)
            } else if (hasFile) {
                formData.append('input_type', 'file')
                formData.append('file_input', this.uploadedFile)
            } else if (hasText) {
                formData.append('input_type', 'text')
                formData.append('text_input', this.textContent)
            }

            var self = this
            var analyzeUrl = window.location.pathname.replace(/\/$/, '') + '/analyze'

            fetch(analyzeUrl, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': Spark.csrfToken,
                    Accept: 'text/event-stream',
                },
                body: formData,
                signal: self.abortController.signal,
            })
                .then(function (response) {
                    if (!response.ok) {
                        if (response.status === 422) {
                            return response.json().then(function (data) {
                                throw new Error(
                                    data.message ||
                                        'Your input was flagged as potentially harmful. Please revise and try again.'
                                )
                            })
                        } else if (response.status === 429) {
                            return response.json().then(function (data) {
                                var retryMsg = ''
                                if (data.retry_after) {
                                    var mins = Math.ceil(data.retry_after / 60)
                                    retryMsg =
                                        ' Please try again in ' +
                                        mins +
                                        ' minute' +
                                        (mins !== 1 ? 's' : '') +
                                        '.'
                                }
                                throw new Error('Rate limit reached.' + retryMsg)
                            })
                        } else if (response.status === 503) {
                            self.serviceAvailable = false
                            throw new Error(
                                'AI service is temporarily unavailable. Please try again later.'
                            )
                        } else {
                            throw new Error('An error occurred while analyzing. Please try again.')
                        }
                    }

                    var reader = response.body.getReader()
                    var decoder = new TextDecoder()
                    var buffer = ''

                    function processStream() {
                        return reader.read().then(function (result) {
                            if (result.done) {
                                self.isAnalyzing = false
                                return
                            }

                            buffer += decoder.decode(result.value, { stream: true })
                            var lines = buffer.split('\n')
                            buffer = lines.pop() // Keep incomplete line in buffer

                            for (var i = 0; i < lines.length; i++) {
                                var line = lines[i].trim()
                                if (line.indexOf('data: ') === 0) {
                                    try {
                                        var payload = JSON.parse(line.substring(6))
                                        if (payload.text) {
                                            self.aiResponse += payload.text
                                        }
                                        if (payload.error) {
                                            self.error = payload.error
                                            self.isAnalyzing = false
                                            return
                                        }
                                        if (payload.done) {
                                            self.isAnalyzing = false
                                            return
                                        }
                                    } catch (e) {
                                        // Skip malformed JSON lines
                                    }
                                }
                            }

                            return processStream()
                        })
                    }

                    return processStream()
                })
                .catch(function (err) {
                    if (err.name === 'AbortError') {
                        self.isAnalyzing = false
                        return
                    }
                    self.error = err.message || 'An unexpected error occurred.'
                    self.isAnalyzing = false
                })
        },

        cancelAnalysis() {
            if (this.abortController) {
                this.abortController.abort()
                this.abortController = null
            }
            this.isAnalyzing = false
        },

        copyResponse() {
            var text = this.$el.querySelector('.response-content').innerText
            navigator.clipboard.writeText(text).then(() => {
                // Change button text to "Copied!"
                this.copyButtonText = 'Copied!'

                // Reset back to "Copy" after 2 seconds
                setTimeout(() => {
                    this.copyButtonText = 'Copy'
                }, 2000)
            })
        },
    },

    beforeDestroy() {
        if (this.abortController) {
            this.abortController.abort()
        }
        if (this.filePreviewUrl) {
            URL.revokeObjectURL(this.filePreviewUrl)
        }
    },
}
</script>

<style>
/* Use the existing BetterHype styling patterns — Bootstrap 4 classes + custom */

.ai-tool-page {
    padding: 20px 20px 5rem;
    max-width: 1400px;
    margin: 0 auto;
}

.ai-tool-header {
    margin-bottom: 24px;
}

.ai-tool-header h2 {
    margin-bottom: 4px;
}

.ai-tool-content {
    display: flex;
    gap: 24px;
    min-height: 500px;
}

.ai-tool-input-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow-y: auto;
}

.ai-tool-response-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* ── Upload Progress ─────────────────────────────────────── */

/* ── Section Headers ────────────────────────────────────── */

.ai-tool-section-header {
    font-size: 14px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.ai-tool-section-header .section-optional {
    font-size: 11px;
    font-weight: 400;
    color: #adb5bd;
    margin-left: auto;
}

/* ── Section Divider ───────────────────────────────────── */

.ai-tool-section-divider {
    display: flex;
    align-items: center;
    margin: 16px 0;
}

.ai-tool-section-divider::before,
.ai-tool-section-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #dee2e6;
}

.ai-tool-section-divider span {
    padding: 0 12px;
    font-size: 11px;
    font-weight: 600;
    color: #adb5bd;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

/* ── Text Area ──────────────────────────────────────────── */

.ai-tool-text-area textarea {
    resize: vertical;
    min-height: 80px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-size: 14px;
}

.ai-tool-text-area textarea:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
}

.ai-tool-text-area .text-actions {
    margin-top: 8px;
    display: flex;
    justify-content: flex-end;
}

.ai-tool-input-hint {
    line-height: 1.5;
}

/* ── Upload Progress ─────────────────────────────────────── */

.upload-progress {
    position: relative;
    height: 4px;
    background: #e9ecef;
    border-radius: 2px;
    margin-bottom: 16px;
    overflow: hidden;
}

.upload-progress .progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #007bff, #0056b3);
    border-radius: 2px;
    transition: width 0.3s ease;
    position: relative;
}

.upload-progress .progress-bar::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    animation: progress-shimmer 1.5s infinite;
}

@keyframes progress-shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

.upload-progress .progress-text {
    position: absolute;
    right: 8px;
    top: -20px;
    font-size: 11px;
    font-weight: 600;
    color: #007bff;
}

/* ── Dropzone ─────────────────────────────────────── */

@keyframes dz-float {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-6px);
    }
}

@keyframes dz-pulse-ring {
    0% {
        box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.18);
    }
    70% {
        box-shadow: 0 0 0 14px rgba(0, 123, 255, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
    }
}

.ai-tool-dropzone {
    border: 2px dashed #d0d5dd;
    border-radius: 12px;
    padding: 24px 20px;
    text-align: center;
    cursor: pointer;
    transition:
        border-color 0.25s ease,
        background-color 0.25s ease,
        transform 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(168deg, #f9fafb 0%, #f1f4f9 100%);
    position: relative;
    overflow: hidden;
}

.ai-tool-dropzone::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    background: radial-gradient(circle at 50% 40%, rgba(0, 123, 255, 0.04) 0%, transparent 70%);
    pointer-events: none;
    transition: opacity 0.3s ease;
    opacity: 0;
}

.ai-tool-dropzone:hover {
    border-color: #007bff;
    background: linear-gradient(168deg, #f4f7ff 0%, #eaf0ff 100%);
    transform: translateY(-1px);
}

.ai-tool-dropzone:hover::before {
    opacity: 1;
}

.ai-tool-dropzone.dragging {
    border-color: #007bff;
    border-style: solid;
    background: linear-gradient(168deg, #edf2ff 0%, #dce6ff 100%);
    transform: scale(1.01);
}

.ai-tool-dropzone.dragging .dropzone-icon-ring {
    animation:
        dz-pulse-ring 1s ease infinite,
        dz-float 1.6s ease-in-out infinite;
    background: rgba(0, 123, 255, 0.12);
}

.ai-tool-dropzone.dragging .dropzone-icon-ring i {
    color: #005ec4;
}

.hidden-input {
    display: none !important;
}

.dropzone-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    position: relative;
    z-index: 1;
}

/* icon ring */
.dropzone-icon-ring {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: rgba(0, 123, 255, 0.07);
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        background 0.3s ease,
        transform 0.3s ease;
}

.dropzone-icon-ring i {
    font-size: 22px;
    color: #007bff;
    transition: color 0.25s ease;
}

.ai-tool-dropzone:hover .dropzone-icon-ring {
    background: rgba(0, 123, 255, 0.12);
    transform: translateY(-2px);
}

/* text */
.dropzone-text {
    line-height: 1.5;
}

.dropzone-heading {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #1a1a2e;
}

.dropzone-subtext {
    margin: 2px 0 0;
    font-size: 13px;
    color: #666;
}

.dropzone-subtext strong {
    color: #007bff;
}

/* format chips */
.dropzone-formats {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 2px;
}

.format-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 11px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 500;
    color: #4a5568;
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.06);
    letter-spacing: 0.01em;
    transition:
        background 0.2s ease,
        border-color 0.2s ease;
}

.format-chip i {
    font-size: 12px;
    opacity: 0.7;
}

.ai-tool-dropzone:hover .format-chip {
    background: rgba(0, 123, 255, 0.06);
    border-color: rgba(0, 123, 255, 0.12);
    color: #2b5a8e;
}

/* size limit */
.dropzone-limit {
    font-size: 11px;
    color: #999;
    margin-top: 2px;
}

/* ── File Preview ─────────────────────────────────────── */

.ai-tool-file-preview {
    flex: 1;
    display: flex;
    flex-direction: column;
    animation: dz-fadeUp 0.35s ease both;
}

@keyframes dz-fadeUp {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.ai-tool-preview-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(168deg, #f9fafb 0%, #f1f4f9 100%);
    border-radius: 12px;
    border: 1px solid #e8ecf1;
    padding: 24px;
    margin-bottom: 14px;
    position: relative;
    overflow: hidden;
}

.preview-thumbnail {
    max-width: 100%;
    max-height: 300px;
    border-radius: 6px;
    object-fit: contain;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.preview-pdf {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: #c0392b;
}

.preview-pdf i {
    opacity: 0.85;
}

.preview-pdf span {
    font-size: 13px;
    color: #666;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* bottom bar */
.preview-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: #f9fafb;
    border: 1px solid #e8ecf1;
    border-radius: 10px;
}

.preview-file-info {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.preview-file-icon {
    font-size: 18px;
    color: #007bff;
    flex-shrink: 0;
}

.preview-file-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.file-name {
    font-size: 13px;
    font-weight: 500;
    color: #1a1a2e;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
}

.file-size {
    font-size: 11px;
    color: #999;
    margin-top: 1px;
}

/* remove button */
.btn-remove-file {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #c0392b;
    background: rgba(192, 57, 43, 0.06);
    border: 1px solid rgba(192, 57, 43, 0.12);
    cursor: pointer;
    transition:
        background 0.2s ease,
        border-color 0.2s ease,
        color 0.2s ease;
}

.btn-remove-file:hover {
    background: rgba(192, 57, 43, 0.12);
    border-color: rgba(192, 57, 43, 0.22);
    color: #a93226;
}

.btn-remove-file:active {
    background: rgba(192, 57, 43, 0.18);
}

.ai-tool-text-area {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.ai-tool-text-area textarea {
    flex: 1;
    resize: none;
    font-size: 14px;
}

.text-area-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 6px;
    min-height: 24px;
}

.text-char-count {
    font-size: 11px;
    color: #999;
    display: flex;
    align-items: center;
    gap: 4px;
}

.text-char-count.near-limit {
    color: #e67e22;
}

.text-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
}

.ai-tool-analyze-btn {
    margin-top: 16px;
}

.response-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #eee;
}

.response-header h4 {
    margin: 0;
}

.response-body {
    flex: 1;
    max-height: 600px;
    overflow-y: auto;
}

.response-empty,
.response-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 300px;
    gap: 12px;
}

.response-content {
    line-height: 1.7;
    font-size: 14px;
}

/* Markdown rendered content styles for v-html */
.response-content h1,
.response-content h2,
.response-content h3,
.response-content h4,
.response-content h5,
.response-content h6 {
    margin-top: 1.2em;
    margin-bottom: 0.6em;
    font-weight: 600;
    line-height: 1.3;
    color: #1a1a2e;
}

.response-content h1 {
    font-size: 1.6em;
}
.response-content h2 {
    font-size: 1.35em;
}
.response-content h3 {
    font-size: 1.15em;
}
.response-content h4 {
    font-size: 1.05em;
}
.response-content h5 {
    font-size: 0.95em;
}
.response-content h6 {
    font-size: 0.85em;
    color: #555;
}

.response-content h1:first-child,
.response-content h2:first-child,
.response-content h3:first-child {
    margin-top: 0;
}

.response-content p {
    margin-bottom: 0.75em;
}

.response-content .response-disclaimer {
    font-size: 12px;
    color: #999;
}

.response-content ul,
.response-content ol {
    margin-bottom: 0.75em;
    padding-left: 1.8em;
}

.response-content ul {
    list-style-type: disc;
}

.response-content ol {
    list-style-type: decimal;
}

.response-content li {
    margin-bottom: 0.5em;
    line-height: 1.65;
    padding-left: 4px;
}

.response-content li > ul,
.response-content li > ol {
    margin-top: 0.3em;
    margin-bottom: 0;
}

.response-content code {
    background: #f0f2f5;
    color: #c7254e;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.88em;
}

.response-content pre {
    background: #1e1e2e;
    color: #cdd6f4;
    padding: 16px;
    border-radius: 6px;
    overflow-x: auto;
    margin-bottom: 1em;
    line-height: 1.5;
}

.response-content pre code {
    background: none;
    color: inherit;
    padding: 0;
    border-radius: 0;
    font-size: 0.88em;
}

.response-content blockquote {
    border-left: 4px solid #007bff;
    margin: 0.75em 0;
    padding: 0.5em 1em;
    background: #f8f9ff;
    color: #444;
    border-radius: 0 4px 4px 0;
}

.response-content blockquote p:last-child {
    margin-bottom: 0;
}

.response-content a {
    color: #007bff;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.15s;
}

.response-content a:hover {
    border-bottom-color: #007bff;
}

.response-content table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1em;
    font-size: 0.92em;
}

.response-content th,
.response-content td {
    border: 1px solid #dee2e6;
    padding: 8px 12px;
    text-align: left;
}

.response-content th {
    background: #f0f2f5;
    font-weight: 600;
}

.response-content tr:nth-child(even) {
    background: #fafbfc;
}

.response-content hr {
    border: none;
    border-top: 2px solid #e5e7eb;
    margin: 1.6em 0;
}

.response-content strong {
    font-weight: 700;
    color: #1a1a2e;
}

.response-content .compliance-label {
    display: block;
    font-weight: 700;
    font-size: 0.95em;
    margin: 20px 0 10px;
    padding: 8px 14px;
    border-radius: 6px;
    letter-spacing: 0.02em;
}

.response-content .compliance-non-compliant {
    color: #b91c1c;
    background-color: #fef2f2;
    border-left: 4px solid #dc2626;
}

.response-content .compliance-documentation {
    color: #92400e;
    background-color: #fffbeb;
    border-left: 4px solid #f59e0b;
}

.response-content .compliance-grey-zone {
    background: #fafafa;
    display: block;
    font-weight: 700;
    font-size: 0.95em;
    margin: 20px 0 10px;
    padding: 8px 14px;
    border-radius: 6px;
    letter-spacing: 0.02em;
    color: #6b7280;
    border-left: 4px solid #ccc;
}

.response-content .compliance-recommended {
    color: #065f46;
    background-color: #ecfdf5;
    border-left: 4px solid #10b981;
}

.response-content img {
    max-width: 100%;
    border-radius: 4px;
}

/* Service Unavailable */
.ai-tool-unavailable {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4em 2em;
    text-align: center;
    color: #6c757d;
}

.ai-tool-unavailable .unavailable-icon {
    font-size: 3em;
    color: #adb5bd;
    margin-bottom: 0.5em;
}

.ai-tool-unavailable h3 {
    font-size: 1.25em;
    color: #495057;
    margin-bottom: 0.5em;
}

.ai-tool-unavailable p {
    font-size: 0.95em;
    max-width: 400px;
    line-height: 1.6;
}

/* Action Buttons */
.ai-tool-action-buttons {
    display: flex;
    gap: 10px;
}

.ai-tool-analyze-btn,
.ai-tool-reset-btn {
    text-transform: uppercase;
    border-radius: 0px;
    padding: 15px;
}

.ai-tool-analyze-btn {
    color: #fff;
}

.ai-tool-btn--brand {
    background: rgb(24, 37, 216);
    border-color: rgb(24, 37, 216);
}

.ai-tool-btn--influencer {
    background: rgb(255, 0, 167);
    border-color: rgb(255, 0, 167);
}

.ai-tool-reset-btn {
    margin-top: 16px;
    min-width: 120px;
    background: transparent;
}

.ai-tool-reset-btn--brand {
    color: rgb(24, 37, 216);
    border-color: rgb(24, 37, 216);
}

.ai-tool-reset-btn--influencer {
    color: rgb(255, 0, 168);
    border-color: rgb(255, 0, 168);
}

/* Page Header */
.ai-tool-page-header {
    padding: 2rem 0;
}

.ai-tool-page-title {
    font-style: normal !important;
    text-transform: uppercase;
    font-size: 20px;
}
.ai-tool-page-desc {
    font-style: normal !important;
    margin-top: 0px;
    margin-bottom: 20px;
}
.ai-tool-page-header .text-muted {
    font-style: normal !important;
}

/* Responsive */
@media (max-width: 768px) {
    .ai-tool-content {
        flex-direction: column;
    }
}
</style>
